import { FC, Fragment, useEffect, useState } from 'react';
import { divide, isEqual, multiply } from 'lodash';
import { orderBy } from 'natural-orderby';
import { useSnackbar } from 'notistack';
import { Backdrop, Button, Modal, Tooltip } from '@mui/material';
import SecondTypeButton from 'src/components/Items/button/SecondTypeButton';
import SimpleSelect from 'src/components/Items/select/SimpleSelect';
import SublocationsSelect from 'src/components/Items/select/SublocationsSelect';
import NumberInput from 'src/components/Items/input/NumberInput';
import { useTypedSelector } from 'src/hooks/useTypeSelector';
import { useActions } from 'src/hooks/useActions';
import { dineroMultiply } from 'src/common/utils/dataFormat';
import { getNames } from 'src/common/utils/others';
import { ILocationFetch } from 'src/models/ILocation';
import { INearVan, INearVanFetch } from 'src/models/INearVan';
import Icons from 'src/common/icons';

interface IProps {
  nearVan?: INearVanFetch;
  vanId: number;
  homeLocation: string;
  existLocations: string[];
  close: boolean;
  handleClose: () => void;
}

const NearVanModal: FC<IProps> = ({
  handleClose,
  nearVan,
  vanId,
  homeLocation,
  existLocations,
  close
}) => {
  const initNearVan: INearVan = {
    location: '',
    price: 0,
    currency: 'USD',
    van_id: vanId
  };

  const { enqueueSnackbar } = useSnackbar();
  const { addNearVans, updateNearVan } = useActions();
  const locationList = useTypedSelector(state => state.location.locationList);
  const [data, setData] = useState<INearVan[]>([initNearVan]);
  const [locations, setLocations] = useState<ILocationFetch[]>([]);
  const [check, setCheck] = useState(false);
  const [isError, setIsError] = useState(false);
  const [disable, setDisable] = useState(false);

  useEffect(() => {
    const home = locationList.find(el => el.name === homeLocation);
    if (home) {
      const locationsWithoutHome = locationList.filter(l => l.name !== homeLocation);
      const regions = home.tags.filter(el => (el.category as any) === 'region').map(el => el.name);
      const newArray = locationsWithoutHome.filter(l => {
        const tagNames = l.tags.filter(t => (t.category as any) === 'region').map(t => t.name);
        const intersection = tagNames.filter(v => regions.includes(v));
        return !!intersection.length;
      });
      const sortedLocation = orderBy(
        newArray,
        [location => location.country, location => location.name],
        ['asc', 'asc']
      );
      setLocations(sortedLocation);
    }
  }, [locationList]);

  useEffect(() => {
    if (nearVan) {
      setData([{ ...nearVan, price: divide(nearVan.price, 100) }]);
    }
  }, [nearVan]);

  useEffect(() => {
    setIsError(false);
    data.map(({ location, price }) => {
      (!location || !price) && setIsError(true);
    });
  }, [data]);

  useEffect(() => {
    if (nearVan) {
      const isTheSame = isEqual(nearVan, { ...data[0], price: multiply(data[0].price, 100) });
      if (isTheSame) setDisable(true);
      else setDisable(false);
    }
  }, [nearVan, data]);

  const handleChange = (index: number, key: keyof INearVan) => (value: string | number) => {
    if (key === 'location') {
      const isSelected = data.find(n => n.location === value);
      if (isSelected) return enqueueSnackbar('Location already selected', { variant: 'error' });
      const isExist = existLocations.find(l => l === value);
      if (isExist) return enqueueSnackbar('Location already exist', { variant: 'error' });
    }
    const editedArr: any[] = [...data];
    editedArr[index][key] = value;
    setData(editedArr);
  };

  const removeItem = (index: number) => () => {
    setData([...data.slice(0, index), ...data.slice(index + 1)]);
  };

  const handleAddMore = () => setData([...data, { ...initNearVan }]);

  const onClose = () => {
    setCheck(false);
    setIsError(false);
    setDisable(false);
    setData([initNearVan]);
    handleClose();
  };

  const handleSubmit = () => {
    if (isError) setCheck(true);
    else {
      if (nearVan) {
        enqueueSnackbar('Editing Near Van...');
        updateNearVan(
          {
            ...data[0],
            price: dineroMultiply(data[0].price, 100)
          } as INearVanFetch,
          nearVan.id
        );
      } else {
        const newArray = [] as INearVan[];
        data.forEach(el => {
          newArray.push({ ...el, price: dineroMultiply(Number(el.price), 100) });
        });
        enqueueSnackbar('Adding near van...');
        addNearVans(newArray);
      }
      onClose();
    }
  };

  return (
    <Modal open={close} onClose={onClose} BackdropComponent={Backdrop} closeAfterTransition>
      <div className='modal modal-400 modal-tag'>
        <div className='modal__title'>{`${nearVan ? 'Edit' : 'Add'} near vans`}</div>
        <div className='modal__fields'>
          {data.map(({ location, price }, index: number) => (
            <Fragment key={index}>
              <div className='modal__field'>
                {index > 0 && (
                  <Tooltip title='Delete Near Van'>
                    <div
                      className='icon_stroke modal__field_icon'
                      style={{ top: 'auto' }}
                      onClick={removeItem(index)}
                    >
                      <Icons.DeleteOneRow />
                    </div>
                  </Tooltip>
                )}
                <div>
                  {/* <div className='filter__title'>Location</div>
                  <SublocationsSelect
                    data={location}
                    setData={handleChange(index, 'location')}
                    dataArray={locations}
                    isChooseLocations
                    key={index}
                    check={check}
                  /> */}
                  <SimpleSelect
                    key={index}
                    data={location}
                    setData={handleChange(index, 'location')}
                    dataArray={getNames(locations)}
                    placeholder='Location'
                    title='Location'
                    check={check}
                  />
                </div>
                <NumberInput
                  data={price}
                  setData={handleChange(index, 'price')}
                  check={check}
                  placeholder='Price'
                  title='Price'
                  text
                />
              </div>
            </Fragment>
          ))}
        </div>
        <div className='col-2 modal__buttons'>
          {nearVan ? (
            <SecondTypeButton children='Cancel' onClick={handleClose} />
          ) : (
            <SecondTypeButton children='Add more' onClick={handleAddMore} />
          )}
          <Button variant='contained' children='Save' onClick={handleSubmit} disabled={disable} />
        </div>
      </div>
    </Modal>
  );
};

export default NearVanModal;
